import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import TextareaAutosize from "react-textarea-autosize";
import Tribute from "tributejs";
// import autoCorrectService from "../../../../utils/autocorrect";
import { toast } from "bulma-toast";
import { observer } from "mobx-react-lite";
import { useStore } from "../../../../stores";
import { Recorder } from "../../../../components";
import { logError } from "../../../../services/logging";
import {
  CannedResponse,
  CannedResponseMedia,
  ChatChannel,
  channelMaxTextLength,
} from "../../../../models";
import locale from "../../../../constants/locale";
import ConnectButton from "../ConnectButton";
import styles from "./MessageComposer.module.scss";
import BotmateAIPopover from "../components/BotmateAI/BotmateAIPopover";
import DisconnectButton from "./DisconnectButton";
import DelayDisconnectPopover from "./DelayDisconnectPopover";
import CreateTask from "./CreateTask";
import QuickReplyPopover from "./QuickReplyPopover";
import EventIframe from "./EventIframe";
import GuestUser from "./GuestUser";
import { safeParse } from "../../../../utils/arrays";
import { PreviewFiles } from "./PreviewFiles";
import FileUploader from "./FileUploader";
import { DateTime } from "luxon";
import TextFormatPopover from "./TextFormatPopover";
import EmojiPicker from "./EmojiPicker";
import ContextualReplyPreview from "./ContextualReplyPreview";
import { useSharedFiles } from "../../../../hooks/useSharedFiles";
// import i18n from "../../../../services/localization";

type Props = {
  ongoingCall: boolean;
  embedded?: boolean;
};

const MessageComposer = ({ ongoingCall, embedded }: Props) => {
  const { t } = useTranslation();
  const {
    selectedChat,
    sendMessage,
    messages,
    setReplyToMessage,
    replyToMessage,
  } = useStore("chats");
  const { user } = useStore("auth");
  const sharedFiles = useSharedFiles({});
  const [value, setValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [files, setFiles] = useState<Blob[]>();
  const [pastedFiles, setPastedFiles] = useState<Blob[]>();
  // const [suggestedCorrection, setSuggestedCorrection] = useState<string>("");
  // const [showCorrectionAlert, setShowCorrectionAlert] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    return () => {
      setReplyToMessage(undefined);
    };
  }, [setReplyToMessage]);

  useEffect(() => {
    if (replyToMessage) {
      setFiles([
        {
          name: "reply",
          type: "reply",
          meta: {
            messageId: replyToMessage.id,
            senderId: replyToMessage.senderId,
            message: replyToMessage.text,
          },
        } as any,
      ]);
    }
  }, [replyToMessage]);

  // Create tribute instance using useMemo like in your old code
  const tribute = React.useMemo(
    () =>
      new Tribute({
        trigger: "@",
        menuItemTemplate: (item) => {
          return (item.original as any).value;
        },
        selectTemplate: (item) => {
          return `@[${(item.original as any).key}](${
            (item.original as any).value
          })`;
        },
        values: (text, cb) => {
          if (
            selectedChat?.channel !== ChatChannel.FBComment ||
            !messages?.length
          ) {
            cb([]);
            return;
          }

          const sendersMap = new Map();
          messages
            .filter((f) => f.isFromUser)
            .forEach((message) => {
              if (message.meta?.sender?.id && message.firstName) {
                const fullName = `${message.firstName}${
                  message.lastName ? " " + message.lastName : ""
                }`.trim();
                if (
                  fullName &&
                  !sendersMap.has(message.meta.sender.id) &&
                  fullName.toLowerCase().includes(text.toLowerCase())
                ) {
                  sendersMap.set(message.meta.sender.id, {
                    key: message.meta.sender.id,
                    value: fullName,
                  });
                }
              }
            });

          cb(Array.from(sendersMap.values()));
        },
        allowSpaces: true,
        menuShowMinLength: 0,
        noMatchTemplate: () => "",
        replaceTextSuffix: " ",
      }),
    [messages, selectedChat?.channel]
  );

  const isChatExpired = React.useMemo(() => {
    if (!selectedChat?.userLastMessage) {
      return false;
    }
    try {
      const lastUserMessageDate = DateTime.fromJSDate(
        selectedChat?.userLastMessage
      );
      const now = DateTime.now();
      const diffInHours = now.diff(lastUserMessageDate).as("hours");
      const maxDiffInHours = 24;
      return diffInHours > maxDiffInHours;
    } catch (error) {
      console.log("Error in isDiffIsMoreThen24Hours", error);
    }
  }, [selectedChat?.userLastMessage]);

  const channelIsWhatsApp = [
    ChatChannel.TwilioWhatsApp,
    ChatChannel.MetaWhatsApp,
  ].includes(selectedChat?.channel!);

  const onSelectQuickReply = (quickReply: CannedResponse) => {
    if (quickReply?.media?.length) {
      const loadFiles = async () => {
        try {
          setIsLoading(true);
          if (quickReply.media?.[0]?.type === "emailTemplate") {
            setFiles((quickReply as any).media as Blob[]);
          } else if (
            ["contentTemplate", "waFlow", "waResponse"].includes(
              quickReply.media?.[0]?.type!
            )
          ) {
            setFiles((quickReply as any).media as Blob[]);
          } else {
            const blobs = await Promise.all(
              quickReply.media?.map(async (m: CannedResponseMedia) => {
                const url = await sharedFiles.resolveUrl(m.url);
                const response = await fetch(url);
                if (!response.ok) throw new Error(t(locale.genericError));
                let blob = await response.blob();
                if (m.type === "document" && m.name?.toLowerCase().endsWith(".vcf")) {
                  blob = blob.slice(0, blob.size, "text/vcard");
                }
                if (m.name) (blob as any).name = m.name;
                return blob;
              }) ?? []
            );
            setFiles(blobs as Blob[]);
            if (!quickReply?.hideTextWhileSending) {
              setValue(quickReply.content);
            }
          }
        } catch (error) {
          logError(error);
          toast({ message: t(locale.genericError), type: "is-danger", dismissible: true });
        } finally {
          setIsLoading(false);
        }
      };
      loadFiles();
    } else {
      setValue(quickReply.content);
    }
  };

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value);
    },
    []
  );

  const handleClick =
    useRef<(message: { text?: string; media?: Blob[] }) => Promise<void>>();
  handleClick.current = async (message: { text?: string; media?: Blob[] }) => {
    if (!message.media && !message.text && value && !files) {
      return;
    }

    const isAMessageReply = !!files?.[0]?.type && files[0].type === "reply";

    setIsLoading(true);

    try {
      if (!isAMessageReply && (message.text || value)) {
        await sendMessage(message.text || value);
      }

      if (message.media || files) {
        for (const file of (message.media || files) as any[]) {
          if (isAMessageReply) {
            file.meta = {
              ...file.meta,
              text: message.text || value,
            };
          }
          await sendMessage(file);
        }
      }

      setValue("");
      setFiles(undefined);
      setShowRecorder(false);
      if (textAreaRef?.current) {
        setTimeout(() => {
          textAreaRef.current?.focus();
        }, 10);
      }
    } catch (e: any) {
      toast({
        message: e.message ?? t(locale.genericError),
        position: "top-right",
        dismissible: true,
        type: "is-danger",
        pauseOnHover: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // // Check text for spelling errors and generate suggestions when typing pauses
  // const checkSpellingErrors = useCallback(async (text: string) => {
  //   if (!text.trim()) {
  //     setShowCorrectionAlert(false);
  //     return;
  //   }

  //   autoCorrectService.setLanguage(i18n.language === "es" ? "es" : "en");

  //   try {
  //     // First check if there are any spelling errors
  //     const words = text.split(/\s+/);
  //     let hasErrors = false;

  //     // Check each word
  //     for (const word of words) {
  //       // Skip words with non-alphabetic characters (URLs, codes, etc.)
  //       if (
  //         !/^[a-zA-Z\u00e1\u00e9\u00ed\u00f3\u00fa\u00fc\u00f1\u00c1\u00c9\u00cd\u00d3\u00da\u00dc\u00d1]+$/.test(
  //           word
  //         )
  //       ) {
  //         continue;
  //       }

  //       const isCorrect = await autoCorrectService.checkSpelling(word);
  //       if (!isCorrect) {
  //         hasErrors = true;
  //         break;
  //       }
  //     }

  //     // If there are errors, get the correction and show it automatically
  //     if (hasErrors) {
  //       const correctedText = await autoCorrectService.correctText(text);

  //       // Only show if there's an actual difference
  //       if (correctedText !== text) {
  //         setSuggestedCorrection(correctedText);
  //         setShowCorrectionAlert(true);
  //       } else {
  //         // No suggestions available
  //         setShowCorrectionAlert(false);
  //       }
  //     } else {
  //       // No errors, hide the correction alert
  //       setShowCorrectionAlert(false);
  //     }
  //   } catch (error) {
  //     console.error("Error checking spelling:", error);
  //   }
  // }, []);

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     checkSpellingErrors(value);
  //   }, 800); // Slightly longer delay to avoid too frequent suggestions

  //   return () => clearTimeout(timer);
  // }, [value, checkSpellingErrors]);

  const listener = useCallback(
    (e: any) => {
      if (e.keyCode !== 13) {
        return false;
      }

      // Check if tribute menu is open - if so, don't handle the enter key
      const tributeMenu = document.querySelector(
        ".tribute-container"
      ) as HTMLElement;
      if (tributeMenu && tributeMenu.style.display !== "none") {
        // Let tribute handle the enter key
        return true;
      }

      e.preventDefault();

      if (e.altKey || e.ctrlKey) {
        // Check if we're in a list context and need to preserve formatting
        const textarea = e.currentTarget;
        const cursorPosition = textarea.selectionStart;
        const text = textarea.value;

        // Get the current line by finding the last newline before cursor
        const lastNewlineIndex = text.lastIndexOf("\n", cursorPosition - 1);
        const currentLineStart =
          lastNewlineIndex === -1 ? 0 : lastNewlineIndex + 1;
        const currentLine = text.substring(currentLineStart, cursorPosition);

        // Check for different list patterns
        const bulletListMatch = currentLine.match(/^(\s*)-\s/);
        const numberedListMatch = currentLine.match(/^(\s*)\d+\.\s/);

        if (bulletListMatch) {
          // It's a bullet list, continue the formatting
          const indentation = bulletListMatch[1] || "";
          setValue(
            text.substring(0, cursorPosition) +
              "\n" +
              indentation +
              "- " +
              text.substring(cursorPosition)
          );

          // Set cursor position after the new bullet
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd =
              cursorPosition + 3 + indentation.length;
          }, 0);
        } else if (numberedListMatch) {
          // It's a numbered list, continue with incremented number
          const indentation = numberedListMatch[1] || "";

          // Find what number the current list item is
          const numberMatch = currentLine.match(/^(\s*)(\d+)\.\s/);
          const currentNumber = numberMatch ? parseInt(numberMatch[2], 10) : 0;
          const nextNumber = currentNumber + 1;

          setValue(
            text.substring(0, cursorPosition) +
              "\n" +
              indentation +
              nextNumber +
              ". " +
              text.substring(cursorPosition)
          );

          // Set cursor position after the new number
          const nextNumberStr = nextNumber.toString();
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd =
              cursorPosition + 3 + indentation.length + nextNumberStr.length;
          }, 0);
        } else {
          // Regular text, just add a newline
          setValue(
            text.substring(0, cursorPosition) +
              "\n" +
              text.substring(cursorPosition)
          );

          // Set cursor after the newline
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd =
              cursorPosition + 1;
          }, 0);
        }
      } else {
        // Regular Enter key - send the message
        handleClick.current?.({ text: (e.currentTarget as any).value });
      }
    },
    [setValue]
  );

  const onSendMessage = () => {
    handleClick.current?.({ text: value });
  };

  // const acceptCorrection = () => {
  // setValue(suggestedCorrection);
  // setShowCorrectionAlert(false);
  // };

  const replaceListener = useCallback((e: any) => {
    // When tribute replaces text, update our state with the new value
    setValue(e.target.value);
  }, []);

  useEffect(() => {
    const ref = textAreaRef.current;
    if (ref) {
      // Only attach tribute for FBComment channel
      if (selectedChat?.channel === ChatChannel.FBComment) {
        tribute.attach(ref);
        ref.addEventListener("tribute-replaced", replaceListener);
      }
      ref.addEventListener("keydown", listener);
      return () => {
        if (selectedChat?.channel === ChatChannel.FBComment) {
          tribute.detach(ref);
          ref?.removeEventListener("tribute-replaced", replaceListener);
        }
        ref?.removeEventListener("keydown", listener);
      };
    }
  }, [listener, replaceListener, tribute, selectedChat?.channel]);

  const handleRecording = useCallback((blob: Blob) => {
    setFiles([blob]);
  }, []);

  // const setFilesForUpload = useCallback(
  //   (files: File[]) => {
  //     for (let i = 0; i < files.length; i++) {
  //       const file = files[i];
  //       if (hasSizeLimit(file)) {
  //         return;
  //       }
  //     }
  //     setFiles(files);
  //   },
  //   [hasSizeLimit]
  // );

  const onPaste = (event: any) => {
    //@ts-ignore
    const clipboardData = event.clipboardData || window.clipboardData;
    const items = clipboardData.items;

    try {
      const files = [];

      for (let i = 0; i < items.length; i++) {
        const file = items[i];
        if (file.kind === "file") {
          files.push(file.getAsFile());
        }
      }

      if (files?.length) {
        event.preventDefault();
        setPastedFiles(files);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const guestUsers = safeParse(selectedChat?.data?.guest_users);
  const isGuestUser = guestUsers?.includes(user?.id);

  const currentUserConnectedToTheChat =
    selectedChat?.isConnected &&
    (user?.id === selectedChat?.userConnected || isGuestUser) &&
    selectedChat.requestStatus !== "sent";

  const onAddEmoji = (emoji: string) => {
    const textarea = textAreaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    let newText = value;
    newText = value.substring(0, start) + emoji + value.substring(end);
    setValue(newText);
  };

  const handleFilesDropped = useCallback((event: any) => {
    setPastedFiles(event.detail.files);
  }, []);

  // const handleLargeFileUpload = useCallback((urls: string[]) => {
  //   handleClick.current?.({ text: urls.join("\n") });
  // }, []);

  useEffect(() => {
    document.addEventListener("handleFilesDropped", handleFilesDropped);

    return () => {
      document.removeEventListener("handleFilesDropped", handleFilesDropped);
    };
  }, [handleFilesDropped]);

  return (
    <div className="control py-2">
      {!currentUserConnectedToTheChat && (
        <div
          className={clsx(styles.container, "is-flex is-4 is-mobile", {
            "is-justify-content-end": true,
          })}
        >
          <ConnectButton ongoingCall={ongoingCall} />
        </div>
      )}

      {currentUserConnectedToTheChat && channelIsWhatsApp && isChatExpired && (
        <div
          className="px-4 py-2 has-rounded-corners has-border mb-2"
          style={{ background: "#fff3c4" }}
        >
          <h2 className="is-5 title mb-2" style={{ fontSize: "14px" }}>
            {t(locale.customerServiceWindowExpired)}
          </h2>
          <span className="is-6 subtitle" style={{ fontSize: "12px" }}>
            {t(locale.customerServiceWindowExpiredDescription)}{" "}
            <a
              href="https://business.whatsapp.com/policy"
              rel="noreferrer"
              target="_blank"
            >
              {t(locale.learnMore)}
            </a>
          </span>
        </div>
      )}

      <div
        style={{ display: currentUserConnectedToTheChat ? "" : "none" }}
        className={clsx(styles.messageBox, "has-background-white has-border")}
      >
        <ContextualReplyPreview />
        <div className={styles.relative}>
          <TextFormatPopover
            value={value}
            setValue={setValue as any}
            textAreaRef={textAreaRef}
          >
            {({ onSelect }) => (
              <TextareaAutosize
                ref={textAreaRef}
                className={clsx("textarea has-fixed-size", styles.textarea)}
                placeholder={t(locale.writeMessage)}
                rows={1}
                maxRows={4}
                onChange={handleChange}
                value={value}
                onPaste={onPaste}
                onSelect={onSelect}
                onMouseUp={onSelect}
                onKeyUp={onSelect}
                maxLength={
                  channelMaxTextLength[selectedChat?.channel as ChatChannel]
                }
                disabled={
                  !selectedChat?.isConnected ||
                  isLoading ||
                  (isChatExpired && channelIsWhatsApp)
                }
              />
            )}
          </TextFormatPopover>

          {channelMaxTextLength[selectedChat?.channel as ChatChannel] && (
            <p className="help has-text-right pr-1">
              {value?.length || 0} /{" "}
              {channelMaxTextLength[selectedChat?.channel as ChatChannel]}
            </p>
          )}
        </div>
        {/* {showCorrectionAlert && (
          <div className="notification is-warning is-light mt-2 p-2 is-size-7">
            <p>
              <strong>{t("Spelling correction suggested")}:</strong>
            </p>
            <p className="mb-2">{suggestedCorrection}</p>
            <div className="buttons are-small">
              <button
                className="button is-primary is-small"
                onClick={acceptCorrection}
              >
                {t("Use correction")}
              </button>
            </div>
          </div>
        )} */}
        {!files && showRecorder && (
          <div className={clsx("input is-align-items-center", styles.input)}>
            <Recorder onRecordingAvailable={handleRecording} />
            <span
              className="delete ml-5"
              onClick={() => setShowRecorder(false)}
            ></span>
          </div>
        )}
        {!!files && (
          <PreviewFiles files={files} onRemove={() => setFiles(undefined)} />
        )}
        <div className="is-flex is-justify-content-space-between m-1">
          <div className="is-flex">
            <FileUploader
              value={value}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              showRecorder={showRecorder}
              files={files}
              setCurrent={handleClick.current}
              pastedFiles={pastedFiles}
              setPastedFiles={setPastedFiles}
              setFiles={setFiles}
              setShowRecorder={setShowRecorder}
            />
            <BotmateAIPopover
              setIsLoading={setIsLoading}
              message={textAreaRef.current?.value}
              setMessage={setValue}
            />
            <EmojiPicker onAddEmoji={onAddEmoji} />
            <QuickReplyPopover
              hideQuickReplies={channelIsWhatsApp && isChatExpired}
              isWhatsappChannel={
                selectedChat?.channel === ChatChannel.MetaWhatsApp &&
                !isChatExpired
              }
              onSelectQuickReply={onSelectQuickReply}
            />
            <div className="has-border-left m-2"></div>
            <DelayDisconnectPopover ongoingCall={ongoingCall} />
            <GuestUser />
            <EventIframe position="composer" />
            {!embedded && <CreateTask disabled={ongoingCall} />}
            <DisconnectButton ongoingCall={ongoingCall} />
          </div>

          <button
            onClick={onSendMessage}
            className={clsx(
              "button is-ghost is-no-box-shadow has-text-primary"
            )}
            disabled={!selectedChat?.isConnected || isLoading}
          >
            <span className="icon">
              <img src="/assets/message-send.svg" alt="" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default observer(MessageComposer);
